import { z } from "zod";
/**
 * LP 由来の問い合わせ（返信して終わり）。
 * ポータルの利用申込（承認してアカウントを作る）とは業務フローが違うため、
 * 同じ `status` カラムを使いつつ **kind ごとに許可する遷移を変える**。
 */
export const LP_INQUIRY_KINDS = ["CONTACT", "RESERVATION", "SEMINAR"];
/** ポータルの利用申込。承認 → アカウント発行まで進む。 */
export const APPLICATION_INQUIRY_KINDS = ["ENTERPRISE_APPLICATION", "MEMBER_APPLICATION"];
export function isApplicationInquiryKind(kind) {
    return APPLICATION_INQUIRY_KINDS.includes(kind);
}
/**
 * 許可する状態遷移。**この表が API とフロントの唯一の真実**。
 * 片方だけに分岐を書くと、画面が出したボタンをサーバーが 409 で弾く（押せるのに進めない）。
 *
 * 設計上の要点:
 * - `ACCOUNT_CREATED` は**終端**。戻す遷移を用意しない（戻しても発行済みアカウントは
 *   消えず、状態と実体が食い違うだけ）。アカウントの停止は既存のユーザー管理で行う。
 * - `SPAM` からは `IN_PROGRESS` にだけ戻せる。誤って迷惑扱いにした問い合わせを
 *   救済できないと、取り違えが恒久的な取りこぼしになる（`DONE` / `REJECTED` の
 *   差し戻しと同じ考え方）。
 * - `ACCOUNT_CREATED` へは**この経路では遷移させない**。アカウント発行 API が
 *   ユーザー作成と同一トランザクションで打刻する（`canTransitionInquiry` は
 *   その内部検証にも使うので、表としては許可を残す）。
 */
export const INQUIRY_STATUS_TRANSITIONS = {
    CONTACT: lpTransitions(),
    RESERVATION: lpTransitions(),
    SEMINAR: lpTransitions(),
    ENTERPRISE_APPLICATION: applicationTransitions(),
    MEMBER_APPLICATION: applicationTransitions(),
};
function lpTransitions() {
    return {
        NEW: ["IN_PROGRESS", "DONE", "SPAM"],
        IN_PROGRESS: ["DONE", "SPAM"],
        DONE: ["IN_PROGRESS"],
        SPAM: ["IN_PROGRESS"],
    };
}
function applicationTransitions() {
    return {
        // **申請に「迷惑」は無い。** ポータルの公開フォームはハニーポットとレート制限で
        // 守られており、届くのは実在の申込。審査の結論は承認 / 非承認の 2 つで足りる。
        // 迷惑という第 3 の終端を混ぜると、事務局が「非承認」と使い分ける基準を持てない。
        NEW: ["IN_PROGRESS", "APPROVED", "REJECTED"],
        IN_PROGRESS: ["APPROVED", "REJECTED"],
        // `IN_PROGRESS` への差し戻しが**必須**。承認したがアカウントを発行できない申込
        // （パスワード未設定 / 組織名が無い / そのメールで既にアカウントがある）は
        // 発行 API が恒久的に 409 を返す。ここに ACCOUNT_CREATED しか無いと、
        // 手で指定できる遷移先が空集合になり（ACCOUNT_CREATED は PATCH では受け付けない）、
        // その申込は APPROVED のまま**どこへも動かせなくなる**。
        APPROVED: ["ACCOUNT_CREATED", "IN_PROGRESS"],
        REJECTED: ["IN_PROGRESS"],
        // 新たに SPAM へは進めないが、過去データが SPAM で残っている場合の逃げ道は残す
        // （どこへも動かせない行を作らない）。
        SPAM: ["IN_PROGRESS"],
        // ACCOUNT_CREATED は終端。意図的にキーを置かない。
    };
}
export function canTransitionInquiry(kind, from, to) {
    return (INQUIRY_STATUS_TRANSITIONS[kind][from] ?? []).includes(to);
}
/** ある状態から遷移できる先の一覧（画面のボタン生成に使う）。 */
export function allowedInquiryTransitions(kind, from) {
    return INQUIRY_STATUS_TRANSITIONS[kind][from] ?? [];
}
/**
 * `PATCH /admin/inquiries/:id/status` で**手で指定できる**遷移先。
 *
 * `ACCOUNT_CREATED` を除くのは、この状態が**アカウント発行の副作用としてのみ**
 * 立つべきだから。手で立てられると「ACCOUNT_CREATED なのにユーザーが存在しない」
 * 申込ができてしまい、二重発行の防止（`Inquiry.createdUserId` の unique）も効かなくなる。
 */
/*
 * `NEW`（新着）も含めない。遷移表のどの行にも `NEW` への路は無いので、
 * 受理しても必ず 409 になる。「スキーマは通るのに必ず失敗する値」を残すと、
 * 呼び出し側は「許された遷移のはずが落ちる」と読んで原因を探すことになる。
 * 受理する値と成功しうる値を一致させ、その不変条件はテストで固定する。
 */
export const MANUAL_INQUIRY_STATUS_TARGETS = [
    "IN_PROGRESS",
    "DONE",
    "SPAM",
    "APPROVED",
    "REJECTED",
];
/**
 * 画面のステータス変更 UI が出してよい遷移先。
 *
 * **`allowedInquiryTransitions` をそのまま使ってはいけない。** あちらは
 * 「業務上ありうる遷移」の全体で、`APPROVED → ACCOUNT_CREATED` を含む。
 * その 1 つだけは PATCH では受け付けない（アカウント発行 API の副作用として立つ）ため、
 * メニューにそのまま並べると **押せるのに 400 が返るボタン**になる。
 * 画面はこちらを使い、除外条件が 1 箇所に留まるようにする。
 */
export function manualInquiryTransitions(kind, from) {
    return allowedInquiryTransitions(kind, from).filter((to) => MANUAL_INQUIRY_STATUS_TARGETS.includes(to));
}
/**
 * 理由の入力を必須にする遷移先。
 *
 * **サーバー側で強制する。** 画面だけの規約にすると、API を直接叩けば理由なしで
 * 非承認にでき「なぜ落としたのか」が履歴に残らない。他の不変条件（遷移表・通知先の
 * 上限）はすべてサーバーで守っているので、ここだけ画面依存にしない。
 */
export const INQUIRY_STATUS_REASON_REQUIRED = [
    "REJECTED",
];
export const inquiryStatusChangeSchema = z
    .object({
    status: z.enum(MANUAL_INQUIRY_STATUS_TARGETS),
    /** 遷移理由（非承認の理由など）。対応履歴にそのまま残る。 */
    comment: z.string().trim().max(2000).optional(),
})
    .superRefine((input, ctx) => {
    const required = INQUIRY_STATUS_REASON_REQUIRED.includes(input.status);
    if (required && !input.comment) {
        ctx.addIssue({
            code: "custom",
            path: ["comment"],
            message: "理由を入力してください",
        });
    }
});
export const inquiryAssigneeChangeSchema = z.object({
    /** null で担当を外す。 */
    assigneeId: z.string().trim().min(1).max(64).nullable(),
});
export const inquiryNoteUpdateSchema = z.object({
    /** 空文字は「メモを消す」の意味で受け、サーバー側で null に正規化する。 */
    note: z.string().max(4000),
});
export const inquiryEventTypeEnum = z.enum([
    "STATUS_CHANGED",
    "ASSIGNEE_CHANGED",
    "NOTE_UPDATED",
    "ACCOUNT_CREATED",
    "REPLY_DRAFT_GENERATED",
    "MAIL_SENT",
    "MAIL_FAILED",
]);
/**
 * 対応状況の表示名。**画面もサーバーのエラーメッセージもここを唯一の出所にする。**
 *
 * 別々に持つと必ず言葉がずれる（実際に「未対応」「未回答」「新着」が同居して、
 * 同じ状態を指しているのか判断できない状態になった）。
 *
 * 語彙は **kind で変わる**。値集合は共通だが、LP 由来は「返信して終わり」、
 * ポータル申請は「審査して承認する」で業務が違うため、`IN_PROGRESS` は
 * 前者では「対応中」、後者では「確認中」になる。
 * 一方 `NEW` は「届いたばかり」で意味が同じなので、両方「新着」に揃える。
 */
const LP_INQUIRY_STATUS_LABELS = {
    NEW: "新着",
    IN_PROGRESS: "対応中",
    DONE: "回答済み",
    SPAM: "迷惑",
    // 以下は申請専用の状態。遷移表が LP 由来には許可しないので通常は出ないが、
    // 網羅していないとデータ次第でラベルが空欄になるため埋めておく。
    APPROVED: "承認済み",
    REJECTED: "非承認",
    ACCOUNT_CREATED: "アカウント発行済み",
};
const APPLICATION_INQUIRY_STATUS_LABELS = {
    NEW: "新着",
    IN_PROGRESS: "確認中",
    APPROVED: "承認済み",
    REJECTED: "非承認",
    ACCOUNT_CREATED: "アカウント発行済み",
    // 申請では使わない状態。上と同じ理由で埋めておく。
    SPAM: "迷惑",
    DONE: "回答済み",
};
export function inquiryStatusLabel(kind, status) {
    return isApplicationInquiryKind(kind)
        ? APPLICATION_INQUIRY_STATUS_LABELS[status]
        : LP_INQUIRY_STATUS_LABELS[status];
}
